import { useEffect, useMemo } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { useQuery } from '@apollo/react-hooks';
import { OutageEvent, Query } from '../../../__generated__/pge-types';
import { getAllOutageEvents } from '../../../queries/outage.query';
import { useGetAllPlannedOutageEvents } from './useGetAllPlannedOutageEvents';
import { useErrorNotification } from '../components/common/error-notification';

export function useOutageEventsDetails() {
  const { t } = useTranslation();

  const { data, error, loading, refetch } = useQuery<Query>(getAllOutageEvents);
  const { setErrorNotification } = useErrorNotification();

  const outageEvents =
    (data?.getAllOutageEvents?.events as OutageEvent[]) ?? [];

  const {
    plannedOutageData,
    loading: plannedEventsLoading,
  } = useGetAllPlannedOutageEvents();

  useEffect(() => {
    error && setErrorNotification();
  }, [error]);

  const {
    activeOutages,
    customersAffected,
    percentageAffected,
  } = useMemo(() => {
    const fallbackOutageCount = Number(t('TOTAL_OUTAGES_BY_COUNTY')) || 0;
    const totalActiveOutages =
      fallbackOutageCount > 0 ? fallbackOutageCount : outageEvents.length;

    const totalCustomersAffected = outageEvents.reduce(
      (acc, outage) => acc + (outage.totalCustomersImpacted || 0),
      0,
    );

    const totalCustomerBase = Number(t('TOTAL_PERCENT_CUSTOMERS_COUNT')) || 1;
    const rawPercentage =
      ((totalCustomerBase - totalCustomersAffected) / totalCustomerBase) * 100;
    const totalPercentageAffected = rawPercentage.toFixed(2);

    return {
      activeOutages: totalActiveOutages,
      customersAffected: totalCustomersAffected,
      percentageAffected: totalPercentageAffected,
    };
  }, [outageEvents, t]);

  const getPercentageAffected = () =>
    customersAffected > 0 && Number(percentageAffected) > 99.99
      ? '99.99%'
      : percentageAffected + '%';

  return {
    activeOutages: loading ? '-' : activeOutages,
    customersAffected: loading ? '-' : customersAffected,
    customersWithPower: loading ? '-' : getPercentageAffected(),
    lastUpdatedTime: data?.getAllOutageEvents?.lastUpdateTime,
    totalPlannedOutages: plannedEventsLoading
      ? '-'
      : plannedOutageData?.totalNoOfPlannedOutages,
    loading,
    error,
    refetch,
  };
}
